import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'
import { isActivePath, type NavItem, type QuickAction } from './navigation'
import { QuickActionSheet } from './QuickActions'

/**
 * Mobile navigation — an iOS-style floating glass tab bar, with the actions in
 * the middle.
 *
 * The bottom bar exists because the primary user is holding a phone
 * one-handed next to a machine and the reachable part of that screen is the
 * bottom third — a top drawer would put every destination out of thumb reach.
 *
 * The centre "+" is where the floating report button went. It was the most
 * pressed control in the app and it sat at the thumb; it still does, but now
 * inside the bar instead of over whatever was at the bottom right of the page.
 * For somebody with one thing to start — a worker, who can only report — it
 * opens the report form directly. With more than one it opens a sheet.
 *
 * Three things make it read as a native tab bar rather than a strip of links:
 * it floats clear of the screen edges, the background is translucent and
 * blurred, and the selection is a pill that *glides* between tabs.
 *
 * `env(safe-area-inset-bottom)` keeps it above the home indicator on a
 * notched phone — without it the bar sits under the gesture bar and the first
 * tap is swallowed by the OS.
 */
export function MobileTabBar({ tabs, actions }: { tabs: NavItem[]; actions: QuickAction[] }) {
  const t = useT()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [sheetOpen, setSheetOpen] = useState(false)

  const hasPlus = actions.length > 0
  // The "+" sits in the middle: half the tabs before it, the rest after.
  const split = Math.ceil(tabs.length / 2)
  const slots = tabs.length + (hasPlus ? 1 : 0)

  const activeTab = tabs.findIndex((tab) => isActivePath(tab.to, pathname))
  /*
   * Which slot the glass pill sits over.
   *
   * -1 on a route with no tab of its own — /profil, /skeniraj, a submission
   * detail — and the pill fades out rather than parking on whichever tab
   * happens to be first. Claiming a tab the user is not on is worse than
   * showing none. The "+" takes a slot, so tabs after it move one along.
   */
  const activeSlot = activeTab < 0 ? -1 : activeTab + (hasPlus && activeTab >= split ? 1 : 0)

  const single = actions.length === 1 ? actions[0] : null

  function renderTab(tab: NavItem, index: number) {
    const Icon = tab.icon
    const isActive = index === activeTab

    return (
      <Tooltip key={tab.to}>
        <TooltipTrigger
          render={
            /*
             * A plain className string, not NavLink's `isActive` callback:
             * Base UI's `render` merges a className *string* onto the element
             * and would overwrite the function form.
             */
            <NavLink
              to={tab.to}
              end={tab.to === '/'}
              className={cn(
                'relative z-10 flex flex-1 flex-col items-center justify-center gap-0.5',
                'rounded-[1.4rem] py-2 text-[11px] font-medium transition-colors',
                'focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )}
            />
          }
        >
          <Icon
            className={cn(
              'size-5 transition-transform duration-300 motion-reduce:transition-none',
              isActive && 'scale-110',
            )}
            aria-hidden
          />
          {t(tab.labelKey)}
        </TooltipTrigger>

        {/* Above, necessarily — the bar is already at the bottom edge. */}
        <TooltipContent side="top" sideOffset={10}>
          {t(tab.hintKey)}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:hidden"
        aria-label={t('nav.mainLabel')}
      >
        <div
          className={cn(
            'relative flex w-full max-w-md items-stretch rounded-[1.75rem] p-1',
            // The glass. saturate lifts the colour that the blur washes out,
            // which is the difference between "frosted" and "grey".
            'bg-card/80 shadow-lg backdrop-blur-2xl backdrop-saturate-150',
            'supports-[backdrop-filter]:bg-card/60',
            // A hairline that catches light on the top edge, like the real thing.
            'border border-black/5 dark:border-white/10',
          )}
        >
          {/*
            The liquid pill.

            Position is computed rather than measured: the slots are `flex-1`,
            so each is exactly 1/n of the track and the pill only has to
            translate by its own width per slot. No refs, no resize observer,
            and it is correct on the first frame. The easing overshoots slightly
            — that small settle at the end is what makes it feel like liquid
            finding its level rather than a box sliding.
          */}
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-y-1 left-1 rounded-[1.4rem]',
              'bg-primary/12 ring-primary/25 ring-1',
              'transition-[transform,opacity] duration-500',
              'motion-reduce:transition-none',
              activeSlot < 0 && 'opacity-0',
            )}
            style={{
              width: `calc((100% - 0.5rem) / ${slots})`,
              transform: `translateX(${Math.max(activeSlot, 0) * 100}%)`,
              transitionTimingFunction: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
            }}
          >
            {/* Specular highlight — brighter at the top, like glass. */}
            <span className="absolute inset-0 rounded-[1.4rem] bg-gradient-to-b from-white/25 to-transparent dark:from-white/10" />
          </span>

          {tabs.slice(0, split).map((tab, index) => renderTab(tab, index))}

          {hasPlus && (
            <div className="relative z-10 flex flex-1 items-center justify-center">
              <button
                type="button"
                onClick={() => (single ? navigate(single.to) : setSheetOpen(true))}
                aria-label={t(single ? single.labelKey : 'quick.new')}
                aria-haspopup={single ? undefined : 'dialog'}
                className={cn(
                  'flex size-12 items-center justify-center rounded-full',
                  'bg-primary text-primary-foreground shadow-md shadow-primary/30',
                  'transition-transform active:scale-95 motion-reduce:transition-none',
                  'focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:ring-offset-2 focus-visible:outline-none',
                )}
              >
                <Plus className="size-6" aria-hidden />
              </button>
            </div>
          )}

          {tabs.slice(split).map((tab, index) => renderTab(tab, index + split))}
        </div>
      </nav>

      {hasPlus && !single && (
        <QuickActionSheet actions={actions} open={sheetOpen} onOpenChange={setSheetOpen} />
      )}
    </>
  )
}
